import { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Modal from '@mui/material/Modal';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';

import { colors, zIndex } from 'styles';
import { sendContactForm } from 'client';
import { validateEmail } from 'components/ContactForm/utils';
import { Button } from 'components/Buttons';
import { useIsMobile } from 'hooks/useIsMobile';

import WebImage from 'assets/top-web.webp';

import {
  CloseButton,
  ErrorText,
  Field,
  Form,
  FullWidthRow,
  ModalContent,
  Pill,
  PillsRow,
  TimelineLabel,
  Title,
  WebDecoration,
} from './StarterPackModal.styles';

interface StarterPackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const INDUSTRY_OPTIONS = ['Maritime', 'Defense', 'Aeronautics', 'Other'];
const TIMELINE_OPTIONS = ['Immediately', 'In 2 weeks', 'In 3 months'];

const REQUIRED_MESSAGE = 'This field is required';
const EMAIL_MESSAGE = 'Please enter a valid email address';

const initialErrors = {
  fullName: '',
  title: '',
  companyEmail: '',
  companyName: '',
  industry: '',
  timeline: '',
};

type FieldErrors = typeof initialErrors;

const menuPaperProps = {
  sx: {
    backgroundColor: colors.mainDark,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    marginTop: '4px',
    '& .MuiMenuItem-root': {
      color: colors.white,
      fontSize: '16px',
    },
    '& .MuiMenuItem-root:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: colors.blue,
    },
  },
};

export const StarterPackModal = ({ isOpen, onClose }: StarterPackModalProps) => {
  const location = useLocation();
  const isMobile = useIsMobile();

  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [timeline, setTimeline] = useState('');
  const [message, setMessage] = useState('');

  const [errors, setErrors] = useState<FieldErrors>(initialErrors);
  const [isLoading, setIsLoading] = useState(false);

  const pageName = location.pathname === '/' ? 'homepage' : location.pathname.substring(1);
  const pageTag = `${pageName}-starter-pack-application`;

  const resetForm = useCallback(() => {
    setFullName('');
    setTitle('');
    setCompanyEmail('');
    setCompanyName('');
    setIndustry('');
    setTimeline('');
    setMessage('');
    setErrors(initialErrors);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const clearError = useCallback((key: keyof FieldErrors) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  }, []);

  const validate = useCallback((): boolean => {
    const next: FieldErrors = {
      fullName: fullName.trim() ? '' : REQUIRED_MESSAGE,
      title: title.trim() ? '' : REQUIRED_MESSAGE,
      companyEmail: !companyEmail.trim()
        ? REQUIRED_MESSAGE
        : validateEmail(companyEmail.trim())
        ? ''
        : EMAIL_MESSAGE,
      companyName: companyName.trim() ? '' : REQUIRED_MESSAGE,
      industry: industry ? '' : REQUIRED_MESSAGE,
      timeline: timeline ? '' : REQUIRED_MESSAGE,
    };

    setErrors(next);

    return Object.values(next).every((value) => !value);
  }, [fullName, title, companyEmail, companyName, industry, timeline]);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();

      if (!validate()) return;

      const details = [
        `Title: ${title.trim()}`,
        `Company: ${companyName.trim()}`,
        `Industry: ${industry}`,
        `Project Start: ${timeline}`,
        message.trim() ? `Message: ${message.trim()}` : null,
      ]
        .filter(Boolean)
        .join('\n');

      setIsLoading(true);

      sendContactForm(companyEmail.trim(), pageTag, fullName.trim(), details)
        .then(() => {
          setIsLoading(false);
          handleClose();
        })
        .catch(() => {
          setIsLoading(false);
        });
    },
    [
      validate,
      title,
      companyName,
      industry,
      timeline,
      message,
      companyEmail,
      fullName,
      pageTag,
      handleClose,
    ],
  );

  const handleChange =
    (setter: (value: string) => void, key?: keyof FieldErrors) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(event.target.value);
      if (key) clearError(key);
    };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="starter-pack-modal-title"
      sx={{
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
          },
        },
      }}
    >
      <ModalContent>
        <WebDecoration>
          <img src={WebImage} alt="" aria-hidden="true" />
        </WebDecoration>

        <CloseButton type="button" onClick={handleClose} aria-label="Close form">
          <CloseIcon />
        </CloseButton>

        <Title id="starter-pack-modal-title">
          {'Fast-track Your AI Integration.\nApply for the Starter Pack.'}
        </Title>

        <Form onSubmit={onSubmit} noValidate>
          <Field
            label="Full Name"
            placeholder="Your Full Name"
            value={fullName}
            onChange={handleChange(setFullName, 'fullName')}
            error={!!errors.fullName}
            helperText={errors.fullName}
            disabled={isLoading}
          />
          <Field
            label="Title"
            value={title}
            onChange={handleChange(setTitle, 'title')}
            error={!!errors.title}
            helperText={errors.title}
            disabled={isLoading}
          />
          <Field
            label="Company email"
            value={companyEmail}
            onChange={handleChange(setCompanyEmail, 'companyEmail')}
            error={!!errors.companyEmail}
            helperText={errors.companyEmail}
            disabled={isLoading}
          />
          <Field
            label="Company Name"
            value={companyName}
            onChange={handleChange(setCompanyName, 'companyName')}
            error={!!errors.companyName}
            helperText={errors.companyName}
            disabled={isLoading}
          />
          <Field
            select
            label="Industry"
            value={industry}
            onChange={handleChange(setIndustry, 'industry')}
            error={!!errors.industry}
            helperText={errors.industry}
            disabled={isLoading}
            SelectProps={{
              MenuProps: { sx: { zIndex: zIndex.modal + 1 }, PaperProps: menuPaperProps },
            }}
          >
            {INDUSTRY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Field>

          <FullWidthRow>
            <TimelineLabel>Project Start</TimelineLabel>
            <PillsRow>
              {TIMELINE_OPTIONS.map((option) => (
                <Pill
                  key={option}
                  type="button"
                  $active={timeline === option}
                  aria-pressed={timeline === option}
                  onClick={() => {
                    setTimeline(option);
                    clearError('timeline');
                  }}
                  disabled={isLoading}
                >
                  {option}
                </Pill>
              ))}
            </PillsRow>
            {errors.timeline && <ErrorText>{errors.timeline}</ErrorText>}
          </FullWidthRow>

          <FullWidthRow>
            <Field
              label="How we can help you? (Optional)"
              value={message}
              onChange={handleChange(setMessage)}
              disabled={isLoading}
              multiline
              minRows={3}
            />
          </FullWidthRow>

          <FullWidthRow>
            <Button type="submit" disabled={isLoading} width={isMobile ? 'full' : 240}>
              Submit Application
            </Button>
          </FullWidthRow>
        </Form>
      </ModalContent>
    </Modal>
  );
};
